import { DataFormat } from "utils";
import { Tooltip } from "@mui/material";
import { intl } from "LocaleWrapper";
import { WeightUnit } from "__generated__/enums";
import { getStoredRegionLocale } from "utils/regionUtils";
import ScaleIcon from "@mui/icons-material/Scale";

type CargoActionActualsQuantityProps = {
	measurements: {
		locationWeight?: number | null,
		locationWeightUnit?: WeightUnit | null,
		locationVolume?: number | null,
	},
	cargo?: {
		isWeightPrimary: boolean,
		isVolumePrimary: boolean,
	},
	convertActualsToMton?: boolean,
	grow?: boolean
};

const CargoActionActualsQuantity = ({ cargo, measurements, convertActualsToMton, grow }: CargoActionActualsQuantityProps) => {
	const style = {
		display: "flex",
		alignItems: "center",
		flexGrow: grow ? 1 : 0,
		marginLeft: grow ? "auto" : "inherit",
		justifyContent: "flex-start",
		width: grow ? "100%" : "max-content",
		gap: 8,
	};

	const alignStyle = {
		display: "inline-block",
		minWidth: grow ? 35 : 0,
		textAlign: grow ? "left" as const : "inherit" as const,
	};

	const amountStyle = {
		display: "inline-block",
		minWidth: grow ? 55 : 0,
		textAlign: grow ? "right" as const : "inherit" as const,
	};

	const valueStyle = {
		flexShrink: 0,
		whiteSpace: "nowrap",
	};

	const locationWeight = measurements.locationWeight ?? 0;
	const locationVolume = measurements.locationVolume ?? 0;

	const format = (value: number) => new Intl.NumberFormat(getStoredRegionLocale(), { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value);
	const formatWeight = (value?: number | null) =>
		<><span style={amountStyle}>{format(value ?? 0)}</span> <span style={alignStyle}>{DataFormat.UNITS.MTON}</span></>;
	const formatVolumeM3 = (value?: number | null) =>
		<><span style={amountStyle}>{format(value ?? 0)}</span> <span style={alignStyle}>{DataFormat.UNITS.M3}</span></>;
	const formatVolumeL15 = (value?: number | null) =>
		<><span style={amountStyle}>{format(value ?? 0)}</span> <span style={alignStyle}>L15</span></>;

	const iconStyle = { display: "inline-flex", width: 20, justifyContent: "center" as const, flexShrink: 0 };

	const actualWeightIcon = (
		<span style={iconStyle}>
			<Tooltip title={intl.formatMessage({ id: "LOADED_QUANTITY" })}>
				<ScaleIcon fontSize="small" color="action" />
			</Tooltip>
		</span>
	);

	const actualWeight = convertActualsToMton
		? formatWeight(locationWeight)
		: (
			<>
				<span style={amountStyle}>{format(locationWeight ?? 0)}</span>
				<span style={alignStyle}>{measurements?.locationWeightUnit ?? ""}</span>
			</>
		);

	const actualVolume = convertActualsToMton
		? formatVolumeM3(locationVolume / 1000)
		: formatVolumeL15(locationVolume);

	if (cargo?.isWeightPrimary && !cargo.isVolumePrimary) {
		return <span style={style}>{actualWeightIcon}<span style={valueStyle}>{actualWeight}</span></span>;
	} else if (!cargo?.isWeightPrimary && cargo?.isVolumePrimary) {
		return <span style={style}>{actualWeightIcon}<span style={valueStyle}>{actualVolume}</span></span>;
	} else if (cargo?.isWeightPrimary && cargo.isVolumePrimary) {
		return <span style={style}>{actualWeightIcon}<span style={valueStyle}>{actualWeight} / {actualVolume}</span></span>;
	} else {
		return <>-</>;
	}
};

export default CargoActionActualsQuantity;
